import { FilterBase, OrFilter, TermFilter } from '../../../../data/schema/SubstrateSearchRequest';
import BaseSearchScopeType from 'owa-service/lib/contract/BaseSearchScopeType';
import DistinguishedFolderId from 'owa-service/lib/contract/DistinguishedFolderId';
import ExecuteSearchJsonRequest from 'owa-service/lib/contract/ExecuteSearchJsonRequest';
import FolderId from 'owa-service/lib/contract/FolderId';
import LargeArchiveSearchScopeType from 'owa-service/lib/contract/LargeArchiveSearchScopeType';
import PrimaryMailboxSearchScopeType from 'owa-service/lib/contract/PrimaryMailboxSearchScopeType';
import SearchFolderScopeType from 'owa-service/lib/contract/SearchFolderScopeType';
import SearchScopeArchives from 'owa-service/lib/contract/SearchScopeArchives';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';

const DEFAULT_FOLDERNAME_MAIL: string = 'MsgFolderRoot';

export function createFoldersFilterFromExecuteSearchRequest(
    request: ExecuteSearchJsonRequest
): FilterBase[] {
    // Get folderIds and folderNames from SearchScope.
    const folderInformation = parseSearchScopeForFoldersFilters(request.Body.SearchScope);

    const { folderIds, folderNames } = folderInformation;

    return createFoldersFilter(
        folderIds,
        folderNames,
        request.Body.IncludeDeleted,
        request.Body.ItemTypes !== 'CalendarItems' ? DEFAULT_FOLDERNAME_MAIL : null
    );
}

export default function createFoldersFilter(
    folderIds: string[],
    folderNames: string[],
    includeDeleted: boolean,
    defaultFolderName: string = null
): FilterBase[] {
    const terms: TermFilter[] = [];
    /**
     * If there are no folderIds and no folderNames, then add a default folder
     * filter if present.
     */
    if (folderIds.length === 0 && folderNames.length === 0 && defaultFolderName != null) {
        folderIds.push(folderNameToId(defaultFolderName));
    }

    for (const folderName of folderNames) {
        terms.push({
            Term: {
                DistinguishedFolderName: folderName,
            },
        });
    }

    for (const folderId of folderIds) {
        terms.push({
            Term: {
                FolderId: folderId,
            },
        });
    }

    if (includeDeleted) {
        terms.push({
            Term: {
                DistinguishedFolderName: 'DeletedItems',
            },
        });
    }

    const filterBases: FilterBase[] = [];
    const orFilter: OrFilter = {
        Or: terms,
    };
    filterBases.push(orFilter);

    return terms.length > 0 ? filterBases : null;
}

function parseSearchScopeForFoldersFilters(
    searchScopes: BaseSearchScopeType[]
): { folderIds: string[]; folderNames: string[] } {
    const folderIds: string[] = [];
    const folderNames: string[] = [];

    if (searchScopes && searchScopes.length > 0) {
        for (const searchScope of searchScopes) {
            const searchScopeType = searchScope.__type;

            if (searchScopeType === 'PrimaryMailboxSearchScopeType:#Exchange') {
                addFolderIdsAndNamesFromSearchScope(
                    (searchScope as PrimaryMailboxSearchScopeType).FolderScope,
                    folderIds,
                    folderNames
                );
            } else if (searchScopeType === 'SingleLargeArchiveSearchScopeType:#Exchange') {
                addFolderIdsAndNamesFromSearchScope(
                    (searchScope as PrimaryMailboxSearchScopeType).FolderScope,
                    folderIds,
                    folderNames
                );
            } else if (searchScopeType === 'LargeArchiveSearchScopeType:#Exchange') {
                const largeArchiveSearchScopeType = searchScope as LargeArchiveSearchScopeType;

                if (
                    (largeArchiveSearchScopeType.ArchiveTypes & SearchScopeArchives.MainArchive) ===
                    SearchScopeArchives.MainArchive
                ) {
                    folderNames.push('archivemsgfolderroot');
                }
            }
        }
    }

    return {
        folderIds,
        folderNames,
    };
}

function addFolderIdsAndNamesFromSearchScope(
    folderScope: SearchFolderScopeType,
    folderIds: string[],
    folderNames: string[]
) {
    const baseFolderId = folderScope.BaseFolderId;
    const folderScopeType = folderScope.BaseFolderId.__type;

    if (folderScopeType === 'FolderId:#Exchange') {
        folderIds.push((baseFolderId as FolderId).Id);
    } else if (folderScopeType === 'DistinguishedFolderId:#Exchange') {
        const distinguishedFolderId = (baseFolderId as DistinguishedFolderId).Id;

        /**
         * While we should use FolderId instead of DistinguishedFolderName for
         * 3S filtering, the "All folders" scope is the exception and the service
         * requires it be specified by DistinguishedFolderName instead.
         */
        if (distinguishedFolderId.toLowerCase() === 'msgfolderroot') {
            folderNames.push(distinguishedFolderId);
        } else {
            const folderId = folderNameToId(distinguishedFolderId);
            folderIds.push(folderId);
        }
    }
}
